/**
 * Mesure d'audience (03/10/2026) : Google Analytics 4 en direct (gtag.js,
 * sans Google Tag Manager) et PostHog si sa clé est posée. Rien ne se charge
 * avant l'accord du visiteur (bandeau `CookieConsent`), et seulement en
 * production. Ce module ne contient que la configuration et des fonctions
 * pures (testées par tests/unit/analytics.test.ts) ; le chargement des
 * outils vit dans src/components/site/tracking.ts.
 */

/** Flux Web GA4 de yellowplus.io. */
export const GA_MEASUREMENT_ID = "G-VXVYV7586F";

/** Instance européenne de PostHog (hébergement UE). */
export const POSTHOG_DEFAULT_HOST = "https://eu.i.posthog.com";

/**
 * Durée de vie des cookies Google Analytics : 13 mois (395 jours), la durée
 * maximale que recommande la CNIL, au lieu des 2 ans posés par défaut.
 */
export const GA_COOKIE_EXPIRES_SECONDS = 395 * 24 * 60 * 60;

export type AnalyticsConfig = {
  gaId: string;
  /** PostHog, seulement si `NEXT_PUBLIC_POSTHOG_KEY` est posée. */
  posthog: { key: string; host: string } | null;
};

/** Configuration lue sur l'hébergeur (côté serveur, transmise au bandeau). */
export function analyticsConfig(env: NodeJS.ProcessEnv = process.env): AnalyticsConfig {
  const key = (env.NEXT_PUBLIC_POSTHOG_KEY ?? "").trim();
  const host = (env.NEXT_PUBLIC_POSTHOG_HOST ?? "").trim().replace(/\/+$/, "") || POSTHOG_DEFAULT_HOST;
  return { gaId: GA_MEASUREMENT_ID, posthog: key ? { key, host } : null };
}

/** Noms des outils, pour le bandeau et la politique de confidentialité. */
export function analyticsTools(config: AnalyticsConfig): string[] {
  return config.posthog ? ["Google Analytics", "PostHog"] : ["Google Analytics"];
}

/** « A », « A et B », « A, B et C ». */
export function joinFrench(items: ReadonlyArray<string>): string {
  if (items.length <= 1) return items[0] ?? "";
  return `${items.slice(0, -1).join(", ")} et ${items[items.length - 1]}`;
}

type ConsentValue = "granted" | "denied";

/**
 * État transmis au mode consentement de Google (v2). Le site n'a pas de
 * publicité : les trois signaux publicitaires restent toujours refusés,
 * seule la mesure d'audience suit le choix du visiteur.
 */
export function googleConsent(analytics: boolean): Record<"ad_storage" | "ad_user_data" | "ad_personalization" | "analytics_storage", ConsentValue> {
  return { ad_storage: "denied", ad_user_data: "denied", ad_personalization: "denied", analytics_storage: analytics ? "granted" : "denied" };
}

/** Paramètres de `gtag('config', …)` : ni signaux Google, ni personnalisation publicitaire, cookies de 13 mois. */
export function googleConfig() {
  return { allow_google_signals: false, allow_ad_personalization_signals: false, cookie_expires: GA_COOKIE_EXPIRES_SECONDS };
}

/** Cookies posés par la mesure d'audience : Google Analytics (`_ga`, `_ga_<ID>`, anciens `_gid`, `_gat`) et PostHog (`ph_…`). */
export function isAnalyticsCookie(name: string): boolean {
  return /^(_ga($|_)|_gid$|_gat)/.test(name) || name.startsWith("ph_");
}

/** Clés de stockage local ou de session de PostHog. */
export function isAnalyticsStorageKey(key: string): boolean {
  return key.startsWith("ph_") || key.startsWith("__ph_");
}

/** Noms des cookies d'une chaîne `document.cookie`. */
export function cookieNames(cookie: string): string[] {
  return cookie
    .split(";")
    .map((part) => part.split("=")[0]?.trim() ?? "")
    .filter(Boolean);
}

/**
 * Domaines où un cookie a pu être posé, pour l'effacer partout : l'hôte seul
 * (chaîne vide, sans attribut domain), puis l'hôte et chacun de ses parents
 * d'au moins deux niveaux (`www.yellowplus.io`, `yellowplus.io`). Google et
 * PostHog posent leurs cookies sur le domaine le plus haut possible.
 */
export function cookieDomains(hostname: string): string[] {
  if (!hostname.includes(".") || /^[\d.]+$/.test(hostname) || hostname.includes(":")) return [""];
  const labels = hostname.split(".");
  const domains = [""];
  for (let start = 0; start <= labels.length - 2; start += 1) domains.push(labels.slice(start).join("."));
  return domains;
}

export type TrackedEvent = { event: string; props: Record<string, string> };

/**
 * Clic à mesurer selon la cible du lien : réservation Cal.com
 * (`book_call_click`), téléphone ou email (`contact_click`).
 */
export function linkEvent(href: string): TrackedEvent | null {
  const value = href.trim();
  if (/^tel:/i.test(value)) return { event: "contact_click", props: { method: "phone" } };
  if (/^mailto:/i.test(value)) return { event: "contact_click", props: { method: "email" } };
  if (/^(https?:)?\/\/(www\.)?cal\.com\//i.test(value)) return { event: "book_call_click", props: {} };
  return null;
}
